import React, { useEffect, useState, useRef } from 'react';
import { Chapter } from '../types';
import { ChapterService } from '../services/chapter.service';
import { ChapterNoteService } from '../services/chapternote.service';

// Import custom styles
import '../styles/ChapterPanel.css';

// Import updated utility functions
import { handleBrowseClick, processJsonFile } from '../utils/fileUtils';
import { loadChapterContent, saveNewChapter, updateExistingChapter, deleteChapter } from '../utils/chapterUtils';
import { saveNewNote, updateExistingNote, deleteNote, addNoteAtPosition } from '../utils/noteUtils';
import { saveNewQuestion, updateExistingQuestion, deleteQuestion } from '../utils/questionUtils';
import { updateChapterName, saveProcessedData, closeContent } from '../utils/contentUtils';

interface ChapterPanelProps {
  subjectId: number | null;
  onSelectChapter: (chapterId: number) => void;
}

interface ProcessedData {
  chapter: string;
  notes: Array<{
    id?: number;
    content: string;
    chapter_id?: number;
    display_order?: number;
    questions: Array<{
      id?: number;
      question: string;
      answer: string;
      note_id?: number;
      is_active?: boolean;
    }>;
  }>;
}

const ChapterPanel: React.FC<ChapterPanelProps> = ({ subjectId, onSelectChapter }) => {
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedChapterId, setSelectedChapterId] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [newChapterName, setNewChapterName] = useState<string>('');
  const [editChapterId, setEditChapterId] = useState<number | null>(null);
  const [editChapterName, setEditChapterName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  
  // File processing
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  // Content display state
  const [showContent, setShowContent] = useState<boolean>(false);
  const [contentData, setContentData] = useState<ProcessedData | null>(null);
  const [editableChapterName, setEditableChapterName] = useState<string>('');
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null);
  const [editingQuestionIndices, setEditingQuestionIndices] = useState<{noteIndex: number, questionIndex: number} | null>(null);
  const [isEditingChapterName, setIsEditingChapterName] = useState<boolean>(false);
  const [isNewContent, setIsNewContent] = useState<boolean>(false);
  const [itemSubmitting, setItemSubmitting] = useState<{type: string, index: number, subIndex?: number} | null>(null);

  // Fetch chapters when subjectId changes
  useEffect(() => {
    if (!subjectId) return;
    
    const fetchChapters = async () => {
      try {
        setLoading(true);
        const chaptersData = await ChapterService.getChaptersBySubjectId(subjectId);
        setChapters(chaptersData);
        setSelectedChapterId('');
        setError(null);
        
        // Reset content display
        setShowContent(false);
        setContentData(null);
      } catch (err) {
        console.error('Error fetching chapters:', err);
        setError('Failed to fetch chapters. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchChapters();
  }, [subjectId]);

  // Handle chapter selection
  const handleChapterChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedChapterId(value);
    
    if (!value) {
      // Clear the content view
      setShowContent(false);
      setContentData(null);
      
      // For blank selection, we can notify the parent that no chapter is selected
      onSelectChapter(0);
      return;
    }
    
    const chapterId = parseInt(value, 10);
    
    // We'll handle displaying the content ourselves, so DON'T trigger the parent's
    // onSelectChapter with a valid chapter ID. Instead, pass 0 to tell the parent
    // not to display anything.
    onSelectChapter(0);
    
    // Load chapter content using utility function
    await loadChapterContent(
      chapterId,
      ChapterNoteService,
      setLoading,
      setContentData,
      setEditableChapterName,
      setIsNewContent,
      setShowContent
    );
  };

  // Handle file change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Process JSON file
  const handleProcessFile = () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }
    
    processJsonFile(
      selectedFile,
      setIsProcessing,
      setContentData,
      setEditableChapterName,
      setIsNewContent,
      setShowContent,
      setSelectedChapterId,
      onSelectChapter
    );
  };

  // Add new chapter
  const handleAddChapter = () => {
    setShowAddForm(true);
    setShowEditForm(false);
  };

  // Edit chapter name
  const handleEditChapter = () => {
    if (!selectedChapterId) {
      alert('Please select a chapter to edit');
      return;
    }

    const chapterId = parseInt(selectedChapterId, 10);
    const chapter = chapters.find(c => c.id === chapterId);
    
    if (chapter) {
      setEditChapterId(chapterId);
      setEditChapterName(chapter.name);
      setShowEditForm(true);
      setShowAddForm(false);
    }
  };

  // Handle delete chapter
  const handleDeleteChapter = async () => {
    await deleteChapter(
      selectedChapterId,
      chapters, 
      setChapters,
      setSelectedChapterId,
      setShowContent,
      setContentData,
      setIsSubmitting,
      onSelectChapter
    );
  };

  // Save new chapter
  const handleSaveChapter = async () => {
    if (!subjectId) return;
    
    await saveNewChapter(
      newChapterName,
      subjectId,
      chapters,
      setChapters,
      setShowAddForm,
      setNewChapterName,
      setSelectedChapterId,
      setContentData,
      setEditableChapterName,
      setIsNewContent,
      setShowContent,
      setIsSubmitting,
      onSelectChapter
    );
  };

  // Save edited chapter
  const handleSaveEditChapter = async () => {
    if (!editChapterId) return;
    
    await updateExistingChapter(
      editChapterName,
      editChapterId,
      chapters,
      selectedChapterId,
      contentData,
      setChapters,
      setShowEditForm,
      setEditChapterId,
      setEditChapterName,
      setContentData,
      setEditableChapterName,
      setIsSubmitting
    );
  };

  // Cancel add chapter
  const handleCancelAddChapter = () => {
    setShowAddForm(false);
    setNewChapterName('');
  };

  // Cancel edit chapter
  const handleCancelEditChapter = () => {
    setShowEditForm(false);
    setEditChapterId(null);
    setEditChapterName('');
  };

  // Handle edit chapter name
  const handleStartEditChapterName = () => {
    setIsEditingChapterName(true);
  };

  const handleSaveChapterName = () => {
    if (isNewContent) {
      // For new content, just update the local state
      if (editableChapterName.trim() === '') {
        alert('Chapter name cannot be empty');
        return;
      }
      setIsEditingChapterName(false);
    } else {
      // For existing chapter, update in the database
      updateChapterName(
        editableChapterName,
        selectedChapterId,
        contentData,
        chapters,
        setIsSubmitting,
        setChapters,
        setContentData,
        setIsEditingChapterName
      );
    }
  };

  const handleCancelEditChapterName = () => {
    if (contentData) {
      setEditableChapterName(contentData.chapter);
    }
    setIsEditingChapterName(false);
  };

  // Handle edit note
  const handleStartEditNote = (noteIndex: number) => {
    setEditingNoteIndex(noteIndex);
  };

  const handleNoteContentChange = (noteIndex: number, newContent: string) => {
    if (!contentData) return;
    
    const updatedNotes = [...contentData.notes];
    updatedNotes[noteIndex] = {
      ...updatedNotes[noteIndex],
      content: newContent
    };
    
    setContentData({
      ...contentData,
      notes: updatedNotes
    });
  };

  // Save note edit
  const handleSaveNoteEdit = async (noteIndex: number) => {
    if (!contentData) return;
    
    const handleSaveNewNoteWrapper = async (index: number) => {
      await saveNewNote(
        index,
        contentData,
        selectedChapterId,
        setItemSubmitting,
        setContentData,
        setEditingNoteIndex
      );
    };
    
    await updateExistingNote(
      noteIndex,
      contentData,
      isNewContent,
      setItemSubmitting,
      setContentData,
      setEditingNoteIndex,
      handleSaveNewNoteWrapper
    );
  };

  // Handle add note
  const handleAddNote = (position: 'start' | 'end' | number = 'end') => {
    if (!contentData) return;
    
    addNoteAtPosition(
      position,
      contentData,
      setContentData,
      setEditingNoteIndex
    );
  };

  // Handle delete note
  const handleDeleteNote = async (noteIndex: number) => {
    await deleteNote(
      noteIndex,
      contentData,
      isNewContent,
      setItemSubmitting,
      setContentData
    );
  };

  // Handle edit question
  const handleStartEditQuestion = (noteIndex: number, questionIndex: number) => {
    setEditingQuestionIndices({ noteIndex, questionIndex });
  };

  const handleQuestionChange = (noteIndex: number, questionIndex: number, field: 'question' | 'answer', value: string) => {
    if (!contentData) return;
    
    const updatedNotes = [...contentData.notes];
    const note = updatedNotes[noteIndex];
    
    if (!note.questions) {
      note.questions = [];
    }
    
    note.questions[questionIndex] = {
      ...note.questions[questionIndex],
      [field]: value
    };
    
    setContentData({
      ...contentData,
      notes: updatedNotes
    });
  };

  // Save question edit
  const handleSaveQuestionEdit = async (noteIndex: number, questionIndex: number) => {
    const handleSaveNewQuestionWrapper = async (nIndex: number, qIndex: number) => {
      await saveNewQuestion(
        nIndex,
        qIndex,
        contentData,
        setItemSubmitting,
        setContentData,
        setEditingQuestionIndices
      );
    };
    
    await updateExistingQuestion(
      noteIndex,
      questionIndex,
      contentData,
      isNewContent,
      setItemSubmitting,
      setContentData,
      setEditingQuestionIndices,
      handleSaveNewQuestionWrapper
    );
  };

  // Handle add question
  const handleAddQuestion = (noteIndex: number) => {
    if (!contentData) return;
    
    const updatedNotes = [...contentData.notes];
    const note = updatedNotes[noteIndex];
    
    if (!note.questions) {
      note.questions = [];
    }
    
    note.questions.push({
      question: '',
      answer: ''
    });
    
    setContentData({
      ...contentData,
      notes: updatedNotes
    });
    
    // Start editing the new question
    const newQuestionIndex = note.questions.length - 1;
    setEditingQuestionIndices({ noteIndex, questionIndex: newQuestionIndex });
  };

  // Handle delete question
  const handleDeleteQuestion = async (noteIndex: number, questionIndex: number) => {
    await deleteQuestion(
      noteIndex,
      questionIndex,
      contentData,
      isNewContent,
      setItemSubmitting,
      setContentData
    );
  };
  
  // Handle save processed data
  const handleSaveProcessedData = async () => {
    if (!subjectId || !contentData) return;
    
    const loadChapterContentWrapper = async (chapterId: number) => {
      await loadChapterContent(
        chapterId,
        ChapterNoteService,
        setLoading,
        setContentData,
        setEditableChapterName,
        setIsNewContent,
        setShowContent
      );
    };
    
    await saveProcessedData(
      contentData,
      editableChapterName,
      subjectId,
      setIsSubmitting,
      setSelectedFile,
      setChapters,
      setSelectedChapterId,
      setShowContent,
      setContentData,
      onSelectChapter,
      loadChapterContentWrapper
    );
  };
  
  // Close content view
  const handleCloseContent = () => {
    closeContent(
      isNewContent,
      setShowContent,
      setContentData,
      setSelectedFile
    );
  };

  if (!subjectId) {
    return null;
  }

  if (loading) {
    return <div className="section">Loading chapters...</div>;
  }

  if (showContent && contentData) {
    return (
      <div className="section">
        <h2>
          {isNewContent ? 'Preview JSON Content' : `Viewing Chapter: ${contentData.chapter}`}
          <button
            className="close-btn"
            onClick={handleCloseContent}
          >
            Back to Chapters
          </button>
        </h2>
        
        <div className="content-container">
          <div className="content-chapter">
            {isEditingChapterName ? (
              <div className="edit-chapter-name">
                <label>Chapter Name:</label>
                <input 
                  type="text" 
                  value={editableChapterName}
                  onChange={(e) => setEditableChapterName(e.target.value)}
                  className="chapter-name-input"
                />
                <div className="edit-buttons">
                  <button 
                    className="save-edit-btn"
                    onClick={handleSaveChapterName}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </button>
                  <button 
                    className="cancel-edit-btn"
                    onClick={handleCancelEditChapterName}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="chapter-name-display">
                <strong>Chapter Name:</strong> {editableChapterName}
                <button 
                  className="edit-btn small-btn"
                  onClick={handleStartEditChapterName}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
          
          <div className="content-notes">
            <strong>Notes and Questions:</strong>
            
            {/* Add note at beginning button */}
            <button
              className="add-question-btn small-btn"
              onClick={() => handleAddNote('start')}
              style={{ marginTop: '10px', marginBottom: '15px' }}
            >
              Add Note Here
            </button>
            
            {contentData.notes.length === 0 && (
              <div className="no-notes">
                <p>No notes available. Click "Add Note" to create a new note.</p>
              </div>
            )}
            
            {contentData.notes.map((note, noteIndex) => (
              <React.Fragment key={noteIndex}>
                <div className="content-note">
                  {editingNoteIndex === noteIndex ? (
                    <div className="edit-note-content">
                      <label>Note {noteIndex + 1}:</label>
                      <textarea
                        value={note.content}
                        onChange={(e) => handleNoteContentChange(noteIndex, e.target.value)}
                        className="note-content-input"
                      />
                      <button 
                        className="save-edit-btn"
                        onClick={() => handleSaveNoteEdit(noteIndex)}
                        disabled={
                          itemSubmitting !== null && 
                          itemSubmitting.type === 'note' && 
                          itemSubmitting.index === noteIndex
                        }
                      >
                        {itemSubmitting !== null && 
                         itemSubmitting.type === 'note' && 
                         itemSubmitting.index === noteIndex
                          ? 'Saving...' 
                          : 'Save'}
                      </button>
                    </div>
                  ) : (
                    <div className="content-note-content">
                      <div className="note-header">
                        <strong>Note {noteIndex + 1}:</strong> {note.content}
                        <div className="note-actions">
                          <button 
                            className="edit-btn small-btn"
                            onClick={() => handleStartEditNote(noteIndex)}
                          >
                            Edit
                          </button>
                          <button 
                            className="delete-btn small-btn"
                            onClick={() => handleDeleteNote(noteIndex)}
                            disabled={
                              itemSubmitting !== null && 
                              itemSubmitting.type === 'note' && 
                              itemSubmitting.index === noteIndex
                            }
                          >
                            {itemSubmitting !== null && 
                             itemSubmitting.type === 'note' && 
                             itemSubmitting.index === noteIndex
                              ? 'Deleting...' 
                              : 'Delete'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {note.questions && note.questions.length > 0 && (
                    <div className="content-questions">
                      {note.questions.map((q, qIndex) => (
                        <div key={qIndex} className="content-question">
                          {editingQuestionIndices && 
                           editingQuestionIndices.noteIndex === noteIndex && 
                           editingQuestionIndices.questionIndex === qIndex ? (
                            <div className="edit-question">
                              <div className="question-field">
                                <label>Question:</label>
                                <textarea
                                  value={q.question}
                                  onChange={(e) => handleQuestionChange(noteIndex, qIndex, 'question', e.target.value)}
                                  className="question-input"
                                />
                              </div>
                              <div className="question-field">
                                <label>Answer:</label>
                                <textarea
                                  value={q.answer}
                                  onChange={(e) => handleQuestionChange(noteIndex, qIndex, 'answer', e.target.value)}
                                  className="answer-input"
                                />
                              </div>
                              <button 
                                className="save-edit-btn"
                                onClick={() => handleSaveQuestionEdit(noteIndex, qIndex)}
                                disabled={
                                  itemSubmitting !== null && 
                                  itemSubmitting.type === 'question' && 
                                  itemSubmitting.index === noteIndex &&
                                  itemSubmitting.subIndex === qIndex
                                }
                              >
                                {itemSubmitting !== null && 
                                 itemSubmitting.type === 'question' && 
                                 itemSubmitting.index === noteIndex &&
                                 itemSubmitting.subIndex === qIndex
                                  ? 'Saving...' 
                                  : 'Save'}
                              </button>
                            </div>
                          ) : (
                            <div className="question-display">
                              <div className="question-content">
                                <div><strong>Q:</strong> {q.question}</div>
                                <div><strong>A:</strong> {q.answer}</div>
                              </div>
                              <div className="question-actions">
                                <button 
                                  className="edit-btn small-btn"
                                  onClick={() => handleStartEditQuestion(noteIndex, qIndex)}
                                >
                                  Edit
                                </button>
                                <button 
                                  className="delete-btn small-btn"
                                  onClick={() => handleDeleteQuestion(noteIndex, qIndex)}
                                  disabled={
                                    itemSubmitting !== null && 
                                    itemSubmitting.type === 'question' && 
                                    itemSubmitting.index === noteIndex &&
                                    itemSubmitting.subIndex === qIndex
                                  }
                                >
                                  {itemSubmitting !== null && 
                                   itemSubmitting.type === 'question' && 
                                   itemSubmitting.index === noteIndex &&
                                   itemSubmitting.subIndex === qIndex
                                    ? 'Deleting...' 
                                    : 'Delete'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <button 
                    className="add-question-btn small-btn"
                    onClick={() => handleAddQuestion(noteIndex)}
                    disabled={!isNewContent && !note.id}
                  >
                    Add Question
                  </button>
                </div>
                
                {/* Add note between notes button */}
                <button
                  className="add-question-btn small-btn"
                  onClick={() => handleAddNote(noteIndex + 1)}
                  style={{ marginBottom: '15px' }}
                >
                  Add Note Here
                </button>
              </React.Fragment>
            ))}
            
            {/* Remove the bottom Add Note button */}
          </div>
          
          {isNewContent && (
            <div className="content-actions">
              <button
                className="save-all-btn"
                onClick={handleSaveProcessedData}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving to Database...' : 'Save to Database'}
              </button>
              <button
                className="cancel-btn"
                onClick={handleCloseContent}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="section">
      <h2>Chapters</h2>
      {error && <div className="error">{error}</div>}
      
      <div className="selector">
        <label htmlFor="chapter-select">Select Chapter:</label>
        <select 
          id="chapter-select" 
          value={selectedChapterId} 
          onChange={handleChapterChange}
        >
          <option value="" disabled>Choose a chapter</option>
          {chapters.map(chapter => (
            <option key={chapter.id} value={String(chapter.id)}>
              {chapter.name}
            </option>
          ))}
        </select>
      </div>
      
      <div className="button-container">
        <div className="button-group">
          <button 
            id="add-chapter-btn" 
            className="add-btn"
            onClick={handleAddChapter}
          >
            Add Chapter
          </button>
          
          <button 
            id="edit-chapter-btn" 
            className="edit-btn"
            onClick={handleEditChapter}
            disabled={!selectedChapterId}
          >
            Edit Chapter
          </button>
          
          <button 
            id="delete-chapter-btn" 
            className="delete-btn"
            onClick={handleDeleteChapter}
            disabled={!selectedChapterId}
          >
            Delete Chapter
          </button>
        </div>

        {/* File handling buttons */}
        <div className="file-buttons">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".json"
          />
          <button
            id="browse-btn"
            className="browse-btn"
            onClick={() => handleBrowseClick(fileInputRef)}
            disabled={isProcessing}
          >
            Browse
          </button>
          <button
            id="process-btn"
            className="process-btn"
            onClick={handleProcessFile}
            disabled={!selectedFile || isProcessing || isSubmitting}
          >
            {isProcessing ? 'Processing...' : 'Process'}
          </button>
          {selectedFile && (
            <span className="file-name">{selectedFile.name}</span>
          )}
        </div>
      </div>
      
      {showAddForm && (
        <div id="add-chapter-form" className="form-container">
          <h3>Add New Chapter</h3>
          <input 
            type="text" 
            id="new-chapter-name" 
            placeholder="Chapter Name"
            value={newChapterName}
            onChange={(e) => setNewChapterName(e.target.value)}
            disabled={isSubmitting}
          />
          <div className="form-buttons">
            <button 
              id="save-chapter-btn" 
              onClick={handleSaveChapter}
              disabled={isSubmitting}
              className="btn-spacing"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
            <button 
              id="cancel-chapter-btn" 
              onClick={handleCancelAddChapter}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {showEditForm && (
        <div id="edit-chapter-form" className="form-container">
          <h3>Edit Chapter</h3>
          <input 
            type="text" 
            id="edit-chapter-name" 
            placeholder="Chapter Name"
            value={editChapterName}
            onChange={(e) => setEditChapterName(e.target.value)}
            disabled={isSubmitting}
          />
          <div className="form-buttons">
            <button 
              id="save-edit-chapter-btn" 
              onClick={handleSaveEditChapter}
              disabled={isSubmitting}
              className="btn-spacing"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
            <button 
              id="cancel-edit-chapter-btn" 
              onClick={handleCancelEditChapter}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {isProcessing && (
        <div className="processing-overlay">
          <div className="processing-content">
            <div className="loading-spinner"></div>
            <p>Processing JSON file...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChapterPanel;